// Invites referees to a tournament's roster with a fee per game and free-text
// requirements. Accepted referees are not offered: their pay is a rate by now,
// and the backend would leave them alone anyway

import { useMemo, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import InputMoney from './InputMoney';
import MultiSelect from './MultiSelect';
import TextArea from './TextArea';
import Spinner from './Spinner';
import { useModal } from './ModalProvider';
import { useOrgUsers } from '../utils/hooks';
import { RefereeStatuses, UsersRoles } from '../utils/enums';
import { toOptions } from '../utils/misc';
import { tournamentRefereesInvite } from '../utils/firebase';
import './RefereeInviteModalBody.scss';

// The backend's limit, so the field stops where the call would refuse
const requirementsMax = 2000;

export default function RefereeInviteModalBody({ tournamentId, roster }) {
  const [spinner, setSpinner] = useState(false);
  // The body outlives the closed modal; its listener must not
  const { closeModal, isModalOpen } = useModal();
  const { users: referees, isLoaded } = useOrgUsers(
    isModalOpen ? UsersRoles.REFEREE : null,
  );

  const options = useMemo(() => {
    const accepted = new Set(
      roster
        .filter((entry) => entry.status === RefereeStatuses.ACCEPTED)
        .map((entry) => entry.id),
    );
    return toOptions(referees.filter((r) => !accepted.has(r.id)));
  }, [referees, roster]);

  const formik = useFormik({
    initialValues: { refereeIds: [], fee: '', requirements: '' },
    validationSchema: Yup.object({
      refereeIds: Yup.array().min(1, 'Required'),
      fee: Yup.number().typeError('Must be a number').required('Required'),
      requirements: Yup.string().max(requirementsMax, 'Too long'),
    }),
    onSubmit: async ({ refereeIds, fee, requirements }) => {
      setSpinner(true);
      try {
        const { data } = await tournamentRefereesInvite({
          id: tournamentId,
          refereeIds,
          fee: +fee,
          requirements,
        });
        toast.success(
          `Invited ${data.invited.length} referee` +
            (data.invited.length === 1 ? '' : 's'),
        );
        closeModal();
      } catch (error) {
        toast.error(error?.message ?? 'Failed to invite referees');
      } finally {
        setSpinner(false);
      }
    },
  });

  return (
    <form className='referee-invite' onSubmit={formik.handleSubmit}>
      <MultiSelect
        options={options}
        value={formik.values.refereeIds}
        onChange={(value) => formik.setFieldValue('refereeIds', value)}
        placeholder='Referees'
        search
        error={
          formik.touched.refereeIds && formik.errors.refereeIds
            ? formik.errors.refereeIds
            : null
        }
      />
      <InputMoney
        name='fee'
        placeholder='Fee per game'
        value={formik.values.fee}
        onChange={(value) => formik.setFieldValue('fee', value)}
        onBlur={formik.handleBlur}
        error={
          formik.touched.fee && formik.errors.fee ? formik.errors.fee : null
        }
      />
      <TextArea
        name='requirements'
        placeholder='Requirements (optional)'
        rows={4}
        maxLength={requirementsMax}
        {...formik.getFieldProps('requirements')}
        error={
          formik.touched.requirements && formik.errors.requirements
            ? formik.errors.requirements
            : null
        }
      />
      <Spinner show={spinner || !isLoaded} />
      <div className='btns-wrap'>
        <button className='bw' type='button' onClick={closeModal}>
          Cancel
        </button>
        <button type='submit' disabled={spinner}>
          Invite
        </button>
      </div>
    </form>
  );
}
